/** User `type` values used by the backend signup/login payload. */
export const USER_TYPE_DEALER = 1;
export const USER_TYPE_CLIENT = 6;

export function pickUserType(...sources) {
  for (let i = 0; i < sources.length; i += 1) {
    const source = sources[i];
    if (source == null) continue;
    if (typeof source === "number" || typeof source === "string") {
      const asString = String(source).trim().toLowerCase();
      if (asString === "dealer" || asString === "vendor") {
        return USER_TYPE_DEALER;
      }
      if (
        asString === "client" ||
        asString === "customer" ||
        asString === "seller"
      ) {
        return USER_TYPE_CLIENT;
      }
      const num = Number(source);
      if (Number.isFinite(num)) return num;
      continue;
    }
    if (typeof source !== "object") continue;
    const nested = source.user || source.data?.user || source.data || null;
    const raw =
      source.type ??
      source.user_type ??
      source.userType ??
      source.role ??
      source.account_type ??
      (nested &&
        (nested.type ??
          nested.user_type ??
          nested.userType ??
          nested.role ??
          nested.account_type));
    if (raw != null && raw !== "") {
      const asString = String(raw).trim().toLowerCase();
      if (asString === "dealer" || asString === "vendor") {
        return USER_TYPE_DEALER;
      }
      if (
        asString === "client" ||
        asString === "customer" ||
        asString === "seller"
      ) {
        return USER_TYPE_CLIENT;
      }
      const num = Number(raw);
      if (Number.isFinite(num)) return num;
    }
  }
  return null;
}

export function isDealerUser(authOrPayload) {
  const t = pickUserType(authOrPayload);
  if (t == null) return false;
  if (t === USER_TYPE_CLIENT) return false;
  // Signup uses type=1; dealer profile may store business subtypes 1–5 as `type`.
  return Number(t) >= 1 && Number(t) <= 5;
}

export function isClientUser(authOrPayload) {
  return pickUserType(authOrPayload) === USER_TYPE_CLIENT;
}

/** Resolve signup account role from `?role=dealer|client` or `?type=1|6`. */
export function signupRoleFromSearch(search) {
  const raw =
    typeof search === "string"
      ? search
      : typeof window !== "undefined"
        ? window.location.search
        : "";
  try {
    const params = new URLSearchParams(
      raw.startsWith("?") ? raw : `?${raw || ""}`
    );
    const role = String(params.get("role") || "").toLowerCase();
    const type = Number(params.get("type"));
    if (role === "dealer" || type === USER_TYPE_DEALER) return "dealer";
    if (role === "client" || type === USER_TYPE_CLIENT) return "client";
  } catch (e) {
    /* ignore */
  }
  return "client";
}

export function userTypeFromSignupRole(role) {
  return role === "dealer" ? USER_TYPE_DEALER : USER_TYPE_CLIENT;
}

export const DEALERS_LANDING_PATH = "/dealers";
export const CLIENT_LANDING_PATH = "/";

/** Default app home after login/signup for this account type. */
export function homePathForUser(authOrPayload) {
  return isDealerUser(authOrPayload) || isDealerUser(authOrPayload?.user || {})
    ? DEALERS_LANDING_PATH
    : CLIENT_LANDING_PATH;
}

/**
 * Where to send the user after auth. Dealers always land on /dealers unless
 * they were sent to login from a dealers deep link.
 */
export function redirectAfterLogin(authOrPayload, from) {
  const dealer = isDealerUser(authOrPayload) || isDealerUser(authOrPayload?.user || {});
  const fromPath = from?.pathname || "";
  if (dealer) {
    if (fromPath.startsWith("/dealers")) {
      return {
        pathname: from.pathname,
        search: from.search || "",
        hash: from.hash || "",
      };
    }
    return { pathname: DEALERS_LANDING_PATH };
  }
  if (fromPath && !fromPath.startsWith("/dealers")) {
    return {
      pathname: from.pathname,
      search: from.search || "",
      hash: from.hash || "",
    };
  }
  return { pathname: CLIENT_LANDING_PATH };
}
